<script setup lang="ts">
import '@mux/mux-video'
import type MuxVideoElement from '@mux/mux-video'
import { useRuntimeConfig } from '#app'

const { isMobileOrTablet } = useDevice()
const { width: innerWidth } = useWindowSize()

const isMobile = computed(() => {
  if (import.meta.server) return isMobileOrTablet
  return innerWidth.value < 900
})

const config = useRuntimeConfig()
const isDev = config.public.environment !== 'production'

const {
  attr = { loop: true, muted: true, autoplay: true },
  shouldPlayWhileOthersPause = false,
  showPoster = false,
} = defineProps<{
  isMini?: boolean
  attr?: Record<string, boolean>
  alt?: string
  video?: {
    asset: {
      assetId: string
      filename: string | null
      playbackId: string
    }
  } | null
  shouldPlayWhileOthersPause?: boolean
  showPoster?: boolean
}>()

// Refs for the mux-player and container element
const $muxPlayer = ref<MuxVideoElement | null>(null)
const $el = ref<HTMLElement | null>(null)

// State management
const { isFullscreen } = useFullscreen($muxPlayer)

const playing = ref(!!attr.autoplay)
const progress = ref(0)
const seeking = ref(false)
const isIOS = ref(false)
const isReady = ref(false)
const shouldLoadVideo = ref(false) // Flag to conditionally load the video

const handleCanPlay = () => {
  isReady.value = true
  if (attr.autoplay && $muxPlayer.value?.paused) {
    $muxPlayer.value.play().catch(console.error)
  }
}

// Check for iOS user agent
onMounted(() => {
  isIOS.value = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream
})

// Intersection Observer to both trigger preload (with a rootMargin) and auto play/pause
let observer: IntersectionObserver | null = null
onMounted(() => {
  if ($el.value) {
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Trigger video load if near the viewport (using rootMargin)
          if (!shouldLoadVideo.value && entry.isIntersecting) {
            shouldLoadVideo.value = true
          }
          // Only control play/pause if the mux-player is loaded
          if ($muxPlayer.value) {
            if (entry.isIntersecting) {
              // Video is in view: if autoplay is enabled and the video is paused, then play
              if (attr.autoplay && $muxPlayer.value.paused) {
                $muxPlayer.value.play().catch(console.error)
              }
            } else {
              // Video is out of view: if playing, then pause it
              if (!$muxPlayer.value.paused) {
                $muxPlayer.value.pause()
              }
            }
          }
        })
      },
      {
        // Adjust rootMargin so the video loads a bit before coming on screen.
        rootMargin: '800px 0px',
        threshold: 0.25,
      },
    )
    observer.observe($el.value)
  }
})

onUnmounted(() => {
  observer?.disconnect()
})

onMounted(() => {
  if ($muxPlayer.value) {
    $muxPlayer.value.volume = 1
    // For iOS, preload metadata only to reduce memory usage
    if (isIOS.value) {
      $muxPlayer.value.preload = 'metadata'
    }
  }
})

// Progress handling
function handleProgress() {
  if (!$muxPlayer.value || $muxPlayer.value.paused) return
  function updateSeek() {
    if (!$muxPlayer.value || $muxPlayer.value.paused) return
    if (!seeking.value) {
      progress.value = $muxPlayer.value.currentTime / $muxPlayer.value.duration
    }
    requestAnimationFrame(updateSeek)
  }
  updateSeek()
}

function handlePlay() {
  if (!$muxPlayer.value) return
  $muxPlayer.value.play()
  playing.value = true
  isReady.value = true
}

function handlePause() {
  if (!$muxPlayer.value) return
  $muxPlayer.value.pause()
  playing.value = false
}

const fullScreenObjectFit = computed(() => (isFullscreen.value ? 'contain' : 'cover'))

const emit = defineEmits(['duration'])

useListen('should-stop-all-videos', (data) => {
  if (!shouldPlayWhileOthersPause) {
    // console.log('should-stop-all-videos', data)
    handlePause()
    playing.value = false
  }
})
useListen('should-play-all-videos', (data) => {
  // console.log('should-play-all-videos', data)
  handlePlay()
  playing.value = true
})
const showMediaCaption = inject('showMediaCaption', false)
</script>

<template>
  <div ref="$el" class="video relative flex overflow-clip">
    <!-- Conditionally render the mux-player when it should be loaded -->
    <img
      v-if="showPoster && video.asset.playbackId"
      class="h-full w-full"
      :src="`https://image.mux.com/${video.asset.playbackId}/thumbnail.png?width=${isMobile ? 400 : 800}&height=${isMobile ? 200 : 400}&fit_mode=smartcrop&time=${video?.asset?.thumbTime || 0}`"
      alt=""
    />
    <template v-if="shouldLoadVideo">
      <mux-video
        ref="$muxPlayer"
        :style="{
          '--controls': !attr.controls ? 'none' : '',
          '--media-object-fit': fullScreenObjectFit,
          opacity: isReady ? 1 : 0,
          transition: 'opacity 0.8s ease-in-out',
        }"
        :playback-id="
          showMediaCaption
            ? video?.asset.playbackId + '?min_resolution=1080p'
            : video?.asset.playbackId
        "
        stream-type="on-demand"
        :preload="isIOS ? 'metadata' : 'auto'"
        :loop="!isIOS && attr.loop"
        v-bind="attr"
        class="mux-video-element aspect-video h-full w-full"
        poster=""
        playsinline
        :autoplay="attr.autoplay"
        :muted="attr.muted"
        disable-tracking
        @play="handleProgress"
        @pause="handleProgress"
        @canplay="handleCanPlay"
      />
    </template>
  </div>
</template>

<style lang="postcss" scoped>
mux-video {
  /* @media (min-width: 900px) { */
  --controls: none;
  /* } */
  --media-object-fit: cover;
}
</style>
